import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Sparkles, X } from 'lucide-react';
import { Headshot } from './Headshot';
import { InjuryTag } from './InjuryTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const STAT = { points: 'Points', rebounds: 'Rebounds', assists: 'Assists', threes: '3PM', steals: 'Steals', blocks: 'Blocks', stocks: 'Stl+Blk', passing_yards: 'Pass yds', passing_tds: 'Pass TDs', rushing_yards: 'Rush yds', receiving_yards: 'Rec yds', rushing_attempts: 'Rush att', passing_attempts: 'Pass att', passing_completions: 'Completions', receptions: 'Receptions' };
const pct = (p) => `${Math.round((p || 0) * 100)}%`;
const toLeg = (l) => ({
  league: l.league, bet_type: 'player_prop', player_name: l.player, selection: `${l.player} ${l.mark}+ ${l.unit}`, line: l.mark,
  stat_target: l.stat, game_date: l.date, espn_event_id: l.event_id, away_team: l.away_team, home_team: l.home_team,
  athlete_id: l.player_id, team_logo: l.team_logo,
});

const RecipeSummary = ({ r }) => (
  <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="winner-recipe-summary">
    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-bold text-emerald-300">{r.tickets} winning ticket{r.tickets === 1 ? '' : 's'} learned</span>
    {Object.entries(r.mix).slice(0, 4).map(([k, v]) => (
      <span key={k} className="rounded-full bg-zinc-800 px-2 py-0.5 text-zinc-200">{STAT[k] || k} {pct(v)}</span>
    ))}
    <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-zinc-200">lines ~{pct(r.ratio_all)} of his average</span>
    <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-zinc-200">{r.size} legs</span>
    {r.per_game >= 3 && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-300">stacks ~{r.per_game} legs per game</span>}
  </div>
);

const LegLine = ({ l, i, tid, onRemove }) => (
  <li className="flex items-center gap-2 py-1" data-testid={`wr-leg-${tid}-${i}`}>
    {l.league === 'nba' ? <Headshot leg={{ ...l, athlete_id: l.player_id, player_name: l.player }} size={28} badge={false} />
      : l.team_logo && <img src={l.team_logo} alt="" className="h-6 w-6 object-contain" />}
    <div className="min-w-0 flex-1">
      <p className="truncate text-[13px] font-bold text-white">{l.player} <span className="text-emerald-300">{l.mark}+ {l.unit}</span> {l.questionable && <InjuryTag injury={{ status: l.questionable }} testId={`wr-injury-${tid}-${i}`} />}</p>
      <p className="truncate text-[10px] text-zinc-400">{l.team} vs {l.opponent} · avg {l.season_avg} · {pct(l.prob)} to hit</p>
    </div>
    <button type="button" onClick={onRemove} className="rounded p-1 text-zinc-500 hover:text-red-300" aria-label="Remove leg" data-testid={`wr-remove-${tid}-${i}`}><X className="h-3.5 w-3.5" /></button>
  </li>
);

const RecipeTicket = ({ t, tid }) => {
  const [legs, setLegs] = useState(t.legs);
  const [stake, setStake] = useState(t.stake);
  const [state, setState] = useState('');
  const chance = legs.reduce((p, l) => p * l.prob, 1);
  const save = async () => {
    setState('saving');
    try {
      await axios.post(`${API}/parlays`, { platform: 'fanduel', stake, potential_payout: 0, source: 'winner_recipe', legs: legs.map(toLeg) });
      setState('saved');
      toast.success('Saved to your board — add the payout with the pencil once you place it');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setState(''); }
  };
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/15 p-3" data-testid={`wr-ticket-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto text-sm font-black text-white">{t.title} · {legs.length} legs <span className="text-[11px] font-semibold text-zinc-400">all hit <b className="text-amber-300" data-testid={`wr-chance-${tid}`}>{(chance * 100).toFixed(1)}%</b></span></p>
        <input type="number" min="1" value={stake} onChange={(e) => setStake(Number(e.target.value) || 0)} className="w-14 rounded border border-zinc-700 bg-zinc-950 px-1 py-0.5 text-[11px] text-white" data-testid={`wr-stake-${tid}`} />
        <button type="button" onClick={save} disabled={!!state || !legs.length || stake <= 0} data-testid={`wr-save-${tid}`}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[11px] font-black text-black hover:bg-emerald-400 disabled:opacity-50">
          {state === 'saving' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}{state === 'saved' ? 'Saved' : 'Save'}
        </button>
      </div>
      <p className="text-[10px] text-zinc-500">{t.window_label}</p>
      <ol className="mt-1 divide-y divide-zinc-800/80">
        {legs.map((l, i) => <LegLine key={`${l.player_id}-${l.stat}`} l={l} i={i} tid={tid} onRemove={() => setLegs(legs.filter((_, j) => j !== i))} />)}
      </ol>
    </div>
  );
};

// Copies the recipe of every won ticket (newest count most) onto today's games.
export const WinnerRecipe = () => {
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const run = async (league) => {
    setBusy(league);
    try {
      const r = (await axios.get(`${API}/winner-recipe/${league}`)).data;
      setRes({ ...r, league });
      if (!r.tickets.length) toast.info(r.note);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy('');
  };
  return (
    <section className="space-y-2 rounded-xl border border-emerald-500/40 bg-zinc-900/60 p-3" data-testid="winner-recipe-card">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-emerald-300" />
        <p className="mr-auto text-sm font-black text-white">Build like my winners</p>
        {['nba', 'nfl'].map((lg) => (
          <button key={lg} type="button" onClick={() => run(lg)} disabled={!!busy} data-testid={`winner-recipe-build-${lg}`}
            className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-black transition-transform active:scale-95 disabled:opacity-60 ${res?.league === lg ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : 'border-zinc-700 text-white hover:border-zinc-500'}`}>
            {busy === lg && <Loader2 className="h-3 w-3 animate-spin" />}{lg.toUpperCase()}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-400">Learns from all your won tickets (newest count most): which stats, how low the lines sit vs each player's average, and how many legs — then builds today's tickets the same way.</p>
      {res?.recipe.tickets > 0 && <RecipeSummary r={res.recipe} />}
      {res && !res.tickets.length && <p className="text-[11px] text-amber-300" data-testid="winner-recipe-note">{res.note}</p>}
      {res?.tickets.map((t, i) => <RecipeTicket key={`${res.league}-${t.title}`} t={t} tid={i} />)}
    </section>
  );
};
